// ЕДИНСТВЕННЫЙ источник прав админки (карточка rbac-single-source, волна 3).
//
// До 2026-06-12 права жили в четырёх несвязанных местах:
//   1) apps/api/src/plugins/rbac.ts  — гранулярный Permission + ROLE_PERMISSIONS
//      (боевая таблица requirePermissionHook), три permissions делегировались в №2;
//   2) apps/api/src/lib/rbac.ts      — PERMISSIONS_V2/canPerform («канон v2»,
//      грубые имена) + CASHBOX_ROUTE_RBAC;
//   3) apps/admin/src/lib/modules.ts — access-матрица модулей админки;
//   4) apps/admin/src/lib/cashboxApi.ts — копия cashbox-матрицы («Keep in sync»).
// Теперь все четыре потребителя импортируют ОТСЮДА. Контракт закреплён:
//   - packages/shared/src/rbac/rbac.contract.test.ts (матрица роль×permission),
//   - apps/api/src/plugins/rbac.runtime-baseline.test.ts (рантайм-базовая линия
//     до рефакторинга — изменение любой пары требует осознанного коммита).
//
// Грубые canPerform-имена (viewAllOrders, setFinalCost, …) выпилены: их
// единственным боевым потребителем была делегация в hasPermission. Девять
// действий PERMISSIONS_V2 без гранулярного эквивалента (createInvoice,
// verifyInvoice, viewAnalytics, readKnowledgeBase, useTaskTracker,
// scanQrAtPickup, manageCatalogs, receiveParcels, manageCourier) нигде не
// энфорсились рантаймом — это был write-only реестр PRD §2.3; новые
// route-level permissions заводить здесь, гранулярными именами.
export const ALL_PERMISSIONS = [
    "orders:view",
    "orders:status",
    "orders:deliver",
    "orders:set_final_cost",
    "orders:export_csv",
    "settings:view",
    "settings:edit",
    "admin:manage_users",
    "admin:manage_roles",
    "warehouse:view",
    "warehouse:adjust",
    "reception:view",
    "reception:manage",
    "accounts:view",
    "accounts:manage",
    "notifications:view",
    "clients:view",
    "clients:manage",
    "payments:verify",
    "integrations:manage",
];
const _allPermissionsCoverUnion = true;
void _allPermissionsCoverUnion;
// PRD §2.2 / §2.3 admin role matrix (v2). `management` carries everything the
// legacy `owner+manager` had; `admin:manage_roles` остаётся точечным gate
// (только management назначает другого management — enforced в users.ts).
//
// Значения = рантайм-базовая линия 2026-06-12 (см. rbac.runtime-baseline.test.ts).
export const ROLE_PERMISSIONS = {
    management: ALL_PERMISSIONS,
    chats: [
        "orders:view",
        // Решение владельца+Димы 2026-06-13: chats = рядовой ресепшен — «выдавать
        // посылки, общаться в чатах, обрабатывать груз на входе»; сканер выдачи
        // и CSV-выгрузка им нужны (вопрос слияния роли с ресепшеном — позже).
        "orders:deliver",
        "orders:export_csv",
        // orders:set_final_cost ВЫДАН (владелец 2026-06-18, ответ на B9): финальную
        // стоимость ставят consoles + fulfillment + chats.
        "orders:set_final_cost",
        "settings:view",
        "reception:view",
        "reception:manage",
        "notifications:view",
        "clients:view",
        "clients:manage",
    ],
    fulfillment: [
        "orders:view",
        "orders:status",
        "orders:deliver",
        // orders:set_final_cost ВЫДАН (владелец 2026-06-18, B9): финалку ставят
        // consoles + fulfillment + chats (ранее у fulfillment было false —
        // осознанное консолидационное значение, пересмотрено владельцем).
        "orders:set_final_cost",
        "warehouse:view",
        "warehouse:adjust",
        "reception:view",
        "reception:manage",
        "settings:view",
        "notifications:view",
        "clients:view",
        "clients:manage",
    ],
    consoles: [
        "orders:view",
        "orders:status",
        "orders:deliver",
        // orders:set_final_cost ВЫДАН (подтверждено владельцем 2026-06-18, B9 —
        // финалку ставят consoles + fulfillment + chats).
        "orders:set_final_cost",
        "warehouse:view",
        "warehouse:adjust",
        "settings:view",
        "notifications:view",
        "clients:view",
    ],
    bookkeeping: [
        "orders:view",
        "orders:export_csv",
        "settings:view",
        "reception:view",
        "accounts:view",
        "accounts:manage",
        "notifications:view",
        "clients:view",
        "payments:verify",
    ],
    tech_support: ["notifications:view", "clients:view"],
    external_pvz: [],
};
export function hasPermission(role, perm) {
    if (!role)
        return false;
    return ROLE_PERMISSIONS[role]?.includes(perm) ?? false;
}
// Cashbox RBAC mapped onto the v2 (PRD §2.2) admin role enum.
//   management  ← old owner + manager (full access incl. settings + hard_delete)
//   bookkeeping ← old accountant      (audit/report only)
//   chats       ← old reception + staff (создаёт/правит свои операции)
//   fulfillment ← old warehouse        (cashbox недоступна)
//   consoles, tech_support, external_pvz — кассы не касаются.
export const CASHBOX_ROUTE_RBAC = {
    management: [
        "read",
        "report",
        "create",
        "update_own",
        "update_any",
        "delete_own",
        "delete_any",
        "restore",
        "audit",
        "import",
        "hard_delete",
        "settings",
        "amo_config",
    ],
    chats: ["read", "report", "create", "update_own", "delete_own"],
    bookkeeping: ["read", "report", "audit"],
    fulfillment: [],
    consoles: [],
    tech_support: [],
    external_pvz: [],
};
export function canUseCashboxRoute(role, action) {
    if (!role)
        return false;
    return CASHBOX_ROUTE_RBAC[role]?.includes(action) ?? false;
}
export function canAccessCashbox(role) {
    return canUseCashboxRoute(role, "read");
}
export function canEditOthersOperations(role) {
    return canUseCashboxRoute(role, "update_any");
}
export function canManageSettings(role) {
    return canUseCashboxRoute(role, "settings");
}
export function canHardDelete(role) {
    return canUseCashboxRoute(role, "hard_delete");
}
// Module access matrix per PRD §2.3 (mapped to v2 roles).
//   management  ← old owner+manager (full доступ ко всем модулям)
//   chats       ← old reception+staff (ресепшен/первая линия)
//   fulfillment ← old warehouse (склад + сортировка)
//   bookkeeping ← old accountant (счета + отчёты)
//   consoles, tech_support, external_pvz — новые роли (см. PRD §2.2).
export const MODULE_ACCESS = {
    hub: {
        management: "full",
        chats: "full",
        fulfillment: "full",
        consoles: "full",
        bookkeeping: "full",
        tech_support: "full",
        external_pvz: "none",
    },
    reception: {
        management: "full",
        chats: "full",
        fulfillment: "partial",
        consoles: "partial",
        bookkeeping: "partial",
        tech_support: "none",
        external_pvz: "none",
    },
    orders: {
        management: "full",
        // chats: 'full'→'read' (2026-06-12, карточка rbac-single-source, шаг 6,
        // консервативный вариант): у роли нет orders:status — раньше UI показывал
        // мутационные кнопки, дающие 403. Права API не расширялись. Если первая
        // линия должна вести Flex-заявки — решение владельца выдать orders:status
        // и вернуть 'full'.
        chats: "read",
        fulfillment: "read",
        consoles: "read",
        bookkeeping: "read",
        tech_support: "none",
        external_pvz: "none",
    },
    payments: {
        management: "full",
        chats: "read",
        fulfillment: "none",
        consoles: "none",
        bookkeeping: "full",
        tech_support: "none",
        external_pvz: "none",
    },
    accounts: {
        management: "full",
        chats: "none",
        fulfillment: "none",
        consoles: "none",
        bookkeeping: "full",
        tech_support: "none",
        external_pvz: "none",
    },
    cash: {
        management: "full",
        chats: "full",
        fulfillment: "none",
        consoles: "none",
        bookkeeping: "full",
        tech_support: "none",
        external_pvz: "none",
    },
    clients: {
        management: "full",
        chats: "full",
        fulfillment: "read",
        consoles: "read",
        bookkeeping: "read",
        tech_support: "none",
        external_pvz: "none",
    },
    kb: {
        management: "full",
        chats: "read",
        fulfillment: "read",
        consoles: "read",
        bookkeeping: "read",
        tech_support: "full",
        external_pvz: "none",
    },
    tasks: {
        management: "full",
        chats: "own",
        fulfillment: "own",
        consoles: "own",
        bookkeeping: "own",
        tech_support: "own",
        external_pvz: "none",
    },
    warehouse: {
        management: "full",
        chats: "none",
        fulfillment: "full",
        consoles: "full",
        bookkeeping: "none",
        tech_support: "none",
        external_pvz: "none",
    },
};
export const SUBSCREEN_ACCESS = {
    // Personal-QR client scanner → client card → orders.
    "reception.delivery": {
        management: "full",
        chats: "full",
        fulfillment: "none",
        consoles: "none",
        bookkeeping: "none",
        tech_support: "none",
        external_pvz: "none",
    },
    "reception.cash": {
        management: "full",
        chats: "full",
        fulfillment: "none",
        consoles: "none",
        bookkeeping: "full",
        tech_support: "none",
        external_pvz: "none",
    },
    // Забор · «Заборы»: курьерские заборы Flex-заявок.
    "reception.pickups": {
        management: "full",
        chats: "full",
        fulfillment: "full",
        consoles: "none",
        bookkeeping: "none",
        tech_support: "none",
        external_pvz: "none",
    },
    "reception.couriers": {
        management: "full",
        chats: "full",
        fulfillment: "full",
        consoles: "full",
        bookkeeping: "none",
        tech_support: "none",
        external_pvz: "none",
    },
    "reception.kb-shortcut": {
        management: "read",
        chats: "read",
        fulfillment: "read",
        consoles: "read",
        bookkeeping: "read",
        tech_support: "read",
        external_pvz: "none",
    },
    "orders.all": {
        management: "full",
        // chats: 'full'→'read' — синхронно с модулем orders (см. комментарий там).
        chats: "read",
        fulfillment: "read",
        consoles: "read",
        bookkeeping: "read",
        tech_support: "none",
        external_pvz: "none",
    },
};
//# sourceMappingURL=index.js.map